import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Result, Space, Spin, Typography } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import Layout from '../components/Layout';
import api from '../services/api';
import { Test } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import TestPage from './TestPage';

const { Title, Paragraph, Text } = Typography;

/** Opening an email or calendar link reads metadata; only a click mounts the timed session. */
const TestEntry: React.FC<{ testId: string | undefined }> = ({ testId }) => {
  const navigate = useNavigate();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const confirmedRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setTest(null);
    const load = async () => {
      try {
        if (!testId) throw new Error('Missing test id');
        const { data } = await api.get<Test>(`/tests/${testId}`, { timeout: 15000 });
        if (!active) return;
        if (data.id !== Number(testId)) throw new Error('Unexpected test id');
        setTest(data);
      } catch (cause: any) {
        if (!active) return;
        const status = cause?.response?.status;
        setError({
          message: getApiErrorMessage(cause, 'Не удалось загрузить информацию о тесте. Проверьте соединение и повторите загрузку.'),
          retryable: status !== 403 && status !== 404,
        });
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [testId, loadVersion]);

  const confirmStart = useCallback(() => {
    if (confirmedRef.current || !test || !test.questions_count || test.is_archived || test.can_start === false || test.can_open === false) return;
    confirmedRef.current = true;
    setConfirmed(true);
  }, [test]);

  if (confirmed) return <TestPage />;

  if (loading) {
    return <Layout><div style={{ textAlign: 'center', padding: 50 }}>
      <Spin size="large" /><p role="status">Загружаем информацию о тесте…</p>
    </div></Layout>;
  }

  if (error || !test) {
    return <Layout><Result status="warning" title="Не удалось открыть тест" subTitle={error?.message}
      extra={<Space wrap>
        {error?.retryable && <Button type="primary" onClick={() => setLoadVersion((value) => value + 1)}>Повторить загрузку</Button>}
        <Button onClick={() => navigate('/results')}>Посмотреть результаты</Button>
        <Button onClick={() => navigate('/')}>Личный кабинет</Button>
      </Space>} /></Layout>;
  }

  const unavailable = !test.questions_count || test.is_archived || test.can_start === false || test.can_open === false;
  const description = test.description?.trim();

  return <Layout>
    <Card className="test-page-card responsive-container" style={{ maxWidth: 760, margin: '32px auto' }}>
      <Text type="secondary">Перед началом</Text>
      <Title level={2} style={{ marginTop: 8 }}>{test.title}</Title>
      {description && !/^тест (с \d+ вопрос|без вопросов)/i.test(description) && <Paragraph>{description}</Paragraph>}
      <Descriptions column={{ xs: 1, sm: 3 }} style={{ margin: '24px 0' }}>
        <Descriptions.Item label="Вопросов">{test.questions_count ?? 0}</Descriptions.Item>
        <Descriptions.Item label="Время">{test.time_limit_minutes} мин</Descriptions.Item>
        <Descriptions.Item label="Для сдачи">от {test.passing_score ?? 70}%</Descriptions.Item>
      </Descriptions>
      {unavailable
        ? <Alert showIcon type="warning" message="Тест пока недоступен для прохождения" description="Вернитесь в личный кабинет или обратитесь к администратору." />
        : <Alert showIcon type="info" message="Начните, когда будете готовы"
          description="Отсчёт времени новой попытки начнётся только после нажатия кнопки «Начать тест»." />}
      <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 24 }}>
        Если вы уже начинали этот тест, откроется сохранённая попытка. Время начатой попытки продолжает идти.
      </Paragraph>
      <Space wrap size="middle">
        <Button type="primary" size="large" icon={<PlayCircleOutlined />} disabled={Boolean(unavailable)} onClick={confirmStart}>Начать тест</Button>
        <Button size="large" onClick={() => navigate('/')}>Личный кабинет</Button>
      </Space>
    </Card>
  </Layout>;
};

const TestEntryPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  // A different test always requires its own explicit confirmation.
  return <TestEntry key={testId} testId={testId} />;
};

export default TestEntryPage;
